import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, Check, CheckCheck, CheckCircle2, Eye } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABEL = {
  task_blocked: "Blocked / Needs Attention",
  stock_variance: "Physical Stock Variance",
  damage_report: "Damage Report",
  rack_discrepancy: "Rack Discrepancy",
  ready_to_resume: "Ready to Resume",
  order_issue: "Order Issue",
  issue_resolved: "Your Report — Resolved",
};

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = useCallback(() => {
    api.get("/notifications").then((r) => {
      setItems(r.data.notifications || []);
      setUnread(r.data.unread || 0);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const markSeen = (n) => {
    if (n.seen) return;
    api.post(`/notifications/${n.id}/seen`).then(() => load()).catch(() => {});
  };

  const markAllRead = () => {
    if (unread === 0) return;
    api.post("/notifications/seen-all").then(() => { setUnread(0); load(); }).catch(() => {});
  };

  const resolve = (e, n) => {
    e.stopPropagation();
    api.post(`/notifications/${n.id}/resolve`).then(() => { toast.success("Issue marked resolved"); load(); })
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail)));
  };

  const goto = (n) => {
    markSeen(n);
    setOpen(false);
    if (n.type === "ready_to_resume") navigate("/tasks");
    else if (n.task_id) navigate(`/task-admin?task=${n.task_id}`);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell" className="relative p-2 rounded-md hover:bg-slate-800 text-slate-200">
          <Bell className="w-5 h-5" />
          {unread > 0 && (
            <span data-testid="notification-unread-count"
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold grid place-items-center">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <span className="text-sm font-semibold">Notifications</span>
          <button data-testid="notification-mark-all-read" onClick={markAllRead}
            disabled={unread === 0}
            className="text-[11px] text-sky-600 hover:underline disabled:text-slate-300 disabled:no-underline flex items-center gap-1">
            <CheckCheck className="w-3.5 h-3.5" />Mark all read
          </button>
        </div>
        <div className="max-h-[440px] overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-3 py-8 text-center text-sm text-slate-400">No notifications</div>
          ) : items.map((n) => (
            <div key={n.id} data-testid={`notification-${n.id}`} onClick={() => goto(n)}
              className={`px-3 py-2.5 border-b last:border-0 text-sm cursor-pointer hover:bg-slate-50 ${!n.seen ? "bg-sky-50/70" : ""}`}>
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[10px] uppercase tracking-wide text-slate-400">{TYPE_LABEL[n.type] || n.type}</div>
                    {n.resolved && (
                      <span data-testid={`notification-resolved-badge-${n.id}`}
                        className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5">
                        <CheckCircle2 className="w-3 h-3" />Resolved
                      </span>
                    )}
                  </div>
                  <div className={`truncate ${!n.seen ? "font-semibold text-slate-900" : "font-medium text-slate-700"}`}>{n.title}</div>
                  {n.body && <div className="text-xs text-slate-500 line-clamp-2">{n.body}</div>}
                  <div className="flex items-center justify-between mt-1.5 gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      {/* WhatsApp-style read tick (per recipient) */}
                      {n.seen ? (
                        <span data-testid={`notification-tick-${n.id}`} title="Seen"
                          className="inline-flex items-center text-sky-500">
                          <CheckCheck className="w-4 h-4" />
                        </span>
                      ) : (
                        <span data-testid={`notification-tick-${n.id}`} title="Delivered — not yet seen"
                          className="inline-flex items-center text-slate-400">
                          <Check className="w-4 h-4" />
                        </span>
                      )}
                      <span className="text-[10px] text-slate-400 font-mono truncate">{(n.created_at || "").slice(0, 16).replace("T", " ")}</span>
                      {n.recipient_role === "admin" && n.seen_by_count > 0 && (
                        <span data-testid={`notification-seenby-${n.id}`}
                          className="inline-flex items-center gap-0.5 text-[10px] text-slate-400">
                          <Eye className="w-3 h-3" />{n.seen_by_count}
                        </span>
                      )}
                    </div>
                    {!n.resolved && (
                      <button data-testid={`notification-resolve-${n.id}`} onClick={(e) => resolve(e, n)}
                        className="text-[11px] text-emerald-600 hover:underline flex items-center gap-1 flex-shrink-0">
                        <CheckCircle2 className="w-3 h-3" />Resolve
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
